/**
 * Thin client for checkout HTTP routes (`POST /api/checkout/*`).
 *
 * Each route maps to one backend controller (see `GUIDE_handwriting_checkout_api_inventory.md`).
 * The Pinia store owns when to call these; this module does not interpret settlement or entitlements.
 *
 * Accepts camelCase {@link CheckoutContextInput} from the store; serializes to snake_case here so
 * callers never build wire bodies by hand. Inbound responses stay wire-shaped — deserialization is
 * the store's job (see `checkoutApiBoundary.ts`).
 *
 * Journeys outside {@link CHECKOUT_API_JOURNEYS} (today: ecommerce Magento/ACCS designer) never
 * reach the network — server would 400 the same allowlist (TK-13531 / TK-13560).
 */

import { ref } from 'vue'
import { useApiFetch } from '@/composables/useApiFetch'
import type {
    ApplyCouponResponse,
    CheckForCreditsResponse,
    CheckoutContextInput,
    FinalizeCartResponse,
    GetPaymentIntentResponse,
    PlaceOrderInput,
    PlaceOrderResponse,
    PrepareCartResponse,
    SyncReturnAddressResponse,
} from '~~/layers/base/types/checkout'
import type { ApiResponse } from '~~/layers/base/types/apiResponseHandler'
import { serializeCheckoutContext } from '~~/layers/base/utils/checkoutApiBoundary'
import { isCheckoutApiJourney } from '~~/layers/base/utils/checkoutJourneyPolicy'

const CHECKOUT_API_JOURNEY_BLOCKED = new Error('Checkout API is not available for this journey')

function blockedCheckoutApiResponse<T>(): ApiResponse<T, Error> {
    return {
        data: ref(undefined),
        error: ref(CHECKOUT_API_JOURNEY_BLOCKED),
        pending: ref(false),
        refresh: async () => {},
    }
}

export const useCheckoutService = () => {
    const postCheckout = async <T>(path: string, body: CheckoutContextInput) => {
        if (!body.journey || !isCheckoutApiJourney(body.journey)) {
            return blockedCheckoutApiResponse<T>()
        }
        return useApiFetch<T, Error>(`/api/checkout/${path}`, {
            method: 'POST',
            body: serializeCheckoutContext(body),
        })
    }

    /**
     * Same URL for all flows; server runs `prepareGuestCart` today.
     * Will branch on `journey` + session when ecommerce customer cart ships.
     */
    const prepareCart = (body: CheckoutContextInput) =>
        postCheckout<PrepareCartResponse>('prepare-cart', body)

    /** DB-only branch: note credits vs stationery voucher (no ACCS). */
    const checkForCredits = (body: CheckoutContextInput) =>
        postCheckout<CheckForCreditsResponse>('check-for-credits', body)

    /** `finalizeGuestCart` controller: add products, shipping method, fetch totals. */
    const finalizeCart = (body: CheckoutContextInput) =>
        postCheckout<FinalizeCartResponse>('finalize-cart', body)

    /** `placeGuestCart` controller: payment method + ACCS place + builder sync. */
    const placeOrder = (body: PlaceOrderInput) => {
        if (!body.journey || !isCheckoutApiJourney(body.journey)) {
            return blockedCheckoutApiResponse<PlaceOrderResponse>()
        }
        const { paymentMethod, ...context } = body
        return useApiFetch<PlaceOrderResponse, Error>(`/api/checkout/place-cart`, {
            method: 'POST',
            body: {
                ...serializeCheckoutContext(context),
                ...(paymentMethod
                    ? {
                          payment_method: {
                              code: paymentMethod.code,
                              ...(paymentMethod.additional_data
                                  ? { additional_data: paymentMethod.additional_data }
                                  : {}),
                          },
                      }
                    : {}),
            },
        })
    }

    /** Route TBD; store updates `coupon` segment. */
    const applyCoupon = (body: CheckoutContextInput) =>
        postCheckout<ApplyCouponResponse>('apply-coupon', body)

    /** Route TBD; store clears `coupon` segment. */
    const removeCoupon = (body: CheckoutContextInput) =>
        postCheckout<ApplyCouponResponse>('remove-coupon', body)

    /**
     * `syncReturnAddressOnGuestCart` controller: re-push the current return address onto an
     * already-existing ACCS cart (TK-12318). Billing always; shipping when journey maps return →
     * shipping (mirrors `prepareGuestCart`). No-op server-side when nothing is mappable.
     */
    const syncReturnAddress = (body: CheckoutContextInput) =>
        postCheckout<SyncReturnAddressResponse>('sync-return-address', body)

    /**
     * `getPaymentIntent` controller: requests a Stripe `client_secret` from ACCS's Stripe module
     * for the current cart, ahead of `stripe.confirmPayment` on the paid-settlement submit path.
     */
    const getPaymentIntent = (body: CheckoutContextInput) =>
        postCheckout<GetPaymentIntentResponse>('get-payment-intent', body)

    return {
        prepareCart,
        checkForCredits,
        finalizeCart,
        placeOrder,
        applyCoupon,
        removeCoupon,
        syncReturnAddress,
        getPaymentIntent,
    }
}
